<template>
  <div class="qrcode-page">
    <!-- 顶部标题区 -->
    <header class="page-header">
      <div class="header-icon-wrapper">
        <div class="header-icon">📱</div>
      </div>
      <h1 class="page-title">扫码支付</h1>
      <p class="page-subtitle">请使用手机扫码完成支付</p>
      <!-- 倒计时显示 -->
      <div class="header-countdown">
        <span class="countdown-icon">⏰</span>
        <span class="countdown-text">{{ formattedCountdown }}</span>
      </div>
    </header>

    <div class="content-wrapper">
      <!-- 重要警告提示 -->
      <section class="warning-section">
        <div class="warning-card">
          <div class="warning-header">
            <span class="warning-icon">🚫</span>
            <span class="warning-title">重要提示</span>
          </div>
          <div class="warning-content">
            <p class="warning-main">请勿手动刷新或关闭页面</p>
            <p class="warning-sub">完成支付后，页面会自动跳转至完成页面</p>
          </div>
        </div>
      </section>

      <!-- 金额卡片 -->
      <section class="amount-section" v-if="payOrderInfo.amount">
        <div class="amount-card">
          <div class="amount-header">
            <span class="amount-icon">💰</span>
            <span class="amount-label">应付金额</span>
          </div>
          <div class="amount-value">￥{{ formatAmount(payOrderInfo.amount) }}</div>
          <div class="amount-order">订单号：{{ payOrderInfo.mchOrderNo || '-' }}</div>
        </div>
      </section>

      <!-- 二维码展示区域 -->
      <section class="qrcode-section">
        <div class="section-header">
          <span class="section-icon">📲</span>
          <h2 class="section-title">请扫描下方二维码支付</h2>
        </div>
        <div class="qrcode-container">
          <div class="qrcode-wrapper" v-if="payOrderInfo.qrCodeUrl">
            <img :src="payOrderInfo.qrCodeUrl" alt="收款二维码" class="qrcode-image" @error="handleImageError">
            <div class="qrcode-mask" v-if="imageError">
              <span class="error-icon">⚠️</span>
              <span class="error-text">二维码加载失败</span>
            </div>
          </div>
          <div class="qrcode-placeholder" v-else>
            <span class="placeholder-icon">🔍</span>
            <span class="placeholder-text">暂无二维码</span>
          </div>
          <div class="qrcode-tip">
            <span class="tip-icon">💡</span>
            <span class="tip-text">请使用微信/支付宝/云闪付扫一扫</span>
          </div>
        </div>
      </section>

      <!-- 操作步骤引导 -->
      <section class="steps-section">
        <div class="section-header">
          <span class="section-icon">📋</span>
          <h2 class="section-title">支付步骤</h2>
        </div>
        <div class="steps-list">
          <div class="step-item">
            <div class="step-number">1</div>
            <div class="step-content">
              <div class="step-title">打开扫码工具</div>
              <div class="step-desc">打开微信/支付宝/云闪付扫一扫功能</div>
            </div>
          </div>
          <div class="step-item">
            <div class="step-number">2</div>
            <div class="step-content">
              <div class="step-title">扫描二维码</div>
              <div class="step-desc">对准上方二维码进行扫描</div>
            </div>
          </div>
          <div class="step-item">
            <div class="step-number">3</div>
            <div class="step-content">
              <div class="step-title">完成支付</div>
              <div class="step-desc">按照提示完成 <strong class="highlight-amount">￥{{ formatAmount(payOrderInfo.amount) }}</strong> 支付</div>
            </div>
          </div>
          <div class="step-item">
            <div class="step-number">4</div>
            <div class="step-content">
              <div class="step-title">等待确认</div>
              <div class="step-desc">支付完成后请耐心等待，系统会自动确认</div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- 加载遮罩 -->
    <div class="processing-mask" v-if="processing">
      <div class="processing-card">
        <div class="spinner"></div>
        <p class="processing-text">{{ processingText }}</p>
      </div>
    </div>

    <!-- 提示Toast -->
    <transition name="fade">
      <div class="toast" v-if="showToast">
        <div class="toast-content">
          <span class="toast-icon">{{ toastIcon }}</span>
          <span>{{ toastMessage }}</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { getPayOrderInfo, getPayOrderInfoState } from '@/api/api'
import config from '@/config'

export default {
  name: 'CashierFdrQrCode',
  data () {
    return {
      payOrderInfo: {},
      loading: false,
      processing: false,
      processingText: '等待支付确认，请稍候…',
      pollTimer: null,
      showToast: false,
      toastMessage: '',
      toastIcon: '✓',
      countdown: 1800, // 30分钟倒计时（秒）
      countdownTimer: null,
      orderToken: '', // 用于标识当前订单
      imageError: false
    }
  },
  mounted () {
    this.fetchPayOrderInfo()
    this.initCountdown()
    this.startOrderPolling()
  },
  beforeDestroy () {
    this.stopOrderPolling()
    this.stopCountdown()
  },
  computed: {
    formattedCountdown () {
      const minutes = Math.floor(this.countdown / 60)
      const seconds = this.countdown % 60
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }
  },
  methods: {
    fetchPayOrderInfo () {
      getPayOrderInfo().then(res => {
        console.log("获取到的信息{}", res)
        this.payOrderInfo = res || {}
        // 从扩展参数中获取二维码URL和收款方式
        if (res.ext) {
          console.log("获取到的信息{}", res.ext.qrCodeUrl)
          this.payOrderInfo.qrCodeUrl = res.ext.qrCodeUrl
          this.payOrderInfo.paymentMethod = res.ext.paymentMethod
        }
        this.orderToken = res.payOrderId || res.mchOrderNo || ''
      }).catch(err => {
        this.$router.push({ name: config.errorPageRouteName, params: { errInfo: err.msg } })
      })
    },
    initCountdown () {
      const savedData = sessionStorage.getItem('payCountdown')
      if (savedData) {
        try {
          const { orderToken, expireTime } = JSON.parse(savedData)
          const now = Math.floor(Date.now() / 1000)
          const remaining = expireTime - now
          
          if (remaining > 0) {
            this.countdown = remaining
            this.orderToken = orderToken
          } else {
            sessionStorage.removeItem('payCountdown')
          }
        } catch (e) {
          console.error('恢复倒计时失败', e)
        }
      }
      this.startCountdown()
    },
    saveCountdownState () {
      if (this.orderToken) {
        const expireTime = Math.floor(Date.now() / 1000) + this.countdown
        sessionStorage.setItem('payCountdown', JSON.stringify({
          orderToken: this.orderToken,
          expireTime: expireTime
        }))
      }
    },
    startOrderPolling () {
      this.stopOrderPolling()
      this.pollTimer = setInterval(() => {
        getPayOrderInfoState().then(info => {
          const state = info && (info.state !== undefined ? info.state : info.orderState)
          if (state === 2) {
            this.stopOrderPolling()
            this.gotoSuccess(info.returnUrl)
          } else if (state === 3) {
            this.stopOrderPolling()
            this.gotoFail(info.errMsg || '支付失败，请重试', info.returnUrl)
          }
        }).catch(() => {})
      }, 2000)
    },
    stopOrderPolling () {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
      this.processing = false
    },
    startCountdown () {
      this.stopCountdown()
      this.saveCountdownState()
      
      this.countdownTimer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--
          if (this.countdown % 10 === 0) {
            this.saveCountdownState()
          }
        } else {
          this.stopCountdown()
          sessionStorage.removeItem('payCountdown')
          this.gotoFail('订单已超时，请重新下单', this.payOrderInfo.returnUrl)
        }
      }, 1000)
    },
    stopCountdown () {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },
    clearCountdownCache () {
      sessionStorage.removeItem('payCountdown')
    },
    gotoSuccess (returnUrl) {
      this.processing = false
      this.stopCountdown()
      this.clearCountdownCache()
      this.$router.push({
        name: 'PaySuccess',
        query: {
          mchOrderNo: (this.payOrderInfo && this.payOrderInfo.mchOrderNo) || '',
          amount: (this.payOrderInfo && this.payOrderInfo.amount) || '',
          mchName: (this.payOrderInfo && this.payOrderInfo.mchName) || '',
          returnUrl: returnUrl || ''
        }
      })
    },
    gotoFail (errMsg, returnUrl) {
      this.processing = false
      this.stopCountdown()
      this.clearCountdownCache()
      this.$router.push({
        name: 'PayFail',
        query: {
          mchOrderNo: (this.payOrderInfo && this.payOrderInfo.mchOrderNo) || '',
          amount: (this.payOrderInfo && this.payOrderInfo.amount) || '',
          errMsg: errMsg || '支付失败，请稍后再试',
          returnUrl: returnUrl || '',
          token: config.cacheToken || ''
        }
      })
    },
    formatAmount (amount) {
      if (!amount) return '0.00'
      return (amount / 100).toFixed(2)
    },
    handleImageError () {
      this.imageError = true
      this.showToastMessage('⚠️', '二维码加载失败，请刷新重试')
    },
    showToastMessage (icon, message) {
      this.toastIcon = icon
      this.toastMessage = message
      this.showToast = true
      setTimeout(() => {
        this.showToast = false
      }, 2000)
    }
  }
}
</script>

<style scoped>
.qrcode-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f0f6ff 0%, #fafbff 100%);
  padding-bottom: 40px;
}

/* 页面头部 */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 28px 20px 32px;
  text-align: center;
  color: #fff;
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 30% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
  pointer-events: none;
}

.header-icon-wrapper {
  margin-bottom: 10px;
}

.header-icon {
  font-size: 42px;
  display: inline-block;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}

.page-title {
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 6px 0;
  letter-spacing: 0.5px;
}

.page-subtitle {
  font-size: 13px;
  opacity: 0.95;
  margin: 0 0 12px 0;
}

.header-countdown {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  margin-top: 8px;
}

.header-countdown .countdown-icon {
  font-size: 16px;
}

.header-countdown .countdown-text {
  font-family: 'Courier New', monospace;
  letter-spacing: 1px;
}

/* 内容容器 */
.content-wrapper {
  max-width: 600px;
  margin: 0 auto;
  padding: 0 16px;
}

/* 警告提示区域 */
.warning-section {
  margin: -20px 0 20px;
}

/* 金额区域 */
.amount-section {
  margin-bottom: 20px;
}

.amount-card {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 16px;
  padding: 24px 20px;
  color: #fff;
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.3);
  position: relative;
  overflow: hidden;
}

.amount-card::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 60%);
  animation: shimmer 4s ease-in-out infinite;
}

@keyframes shimmer {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  50% { transform: translate(-15%, -15%) rotate(180deg); }
}

.amount-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.amount-icon {
  font-size: 24px;
}

.amount-label {
  font-size: 14px;
  opacity: 0.95;
  font-weight: 500;
}

.amount-value {
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 10px;
  letter-spacing: -0.5px;
}

.amount-order {
  font-size: 12px;
  opacity: 0.9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 二维码区域 */
.qrcode-section,
.steps-section {
  background: #fff;
  border-radius: 16px;
  padding: 24px 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.06);
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f5ff;
}

.section-icon {
  font-size: 24px;
}

.section-title {
  font-size: 17px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.qrcode-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
}

.qrcode-wrapper {
  width: 280px;
  height: 280px;
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.qrcode-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 8px;
}

.qrcode-mask {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #fff;
}

.error-icon {
  font-size: 48px;
}

.error-text {
  font-size: 16px;
  font-weight: 500;
}

.qrcode-placeholder {
  width: 280px;
  height: 280px;
  background: #f3f4f6;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #9ca3af;
}

.placeholder-icon {
  font-size: 64px;
}

.placeholder-text {
  font-size: 16px;
  font-weight: 500;
}

.qrcode-tip {
  margin-top: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-radius: 12px;
  color: #1e40af;
  font-size: 14px;
  font-weight: 500;
}

.tip-icon {
  font-size: 18px;
}

/* 步骤引导 */
.steps-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.step-item {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.step-content {
  flex: 1;
  padding-top: 2px;
}

.step-title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}

.step-desc {
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
}

.highlight-amount {
  color: #10b981;
  font-weight: 700;
}

/* 警告提示 */
.warning-card {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 2px solid #ef4444;
  border-radius: 16px;
  padding: 18px;
  box-shadow: 0 4px 20px rgba(239, 68, 68, 0.2);
  animation: pulse-warning 2.5s ease-in-out infinite;
}

@keyframes pulse-warning {
  0%, 100% {
    box-shadow: 0 4px 20px rgba(239, 68, 68, 0.2);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 6px 24px rgba(239, 68, 68, 0.35);
    transform: scale(1.01);
  }
}

.warning-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.warning-icon {
  font-size: 26px;
  animation: shake 3s ease-in-out infinite;
}

@keyframes shake {
  0%, 100% { transform: rotate(0deg); }
  10%, 30% { transform: rotate(-6deg); }
  20%, 40% { transform: rotate(6deg); }
  50%, 90% { transform: rotate(0deg); }
}

.warning-title {
  font-size: 15px;
  font-weight: 700;
  color: #dc2626;
  margin: 0;
}

.warning-content {
  padding-left: 36px;
}

.warning-main {
  font-size: 14px;
  color: #b91c1c;
  font-weight: 700;
  margin: 0 0 4px 0;
  line-height: 1.5;
}

.warning-sub {
  font-size: 12px;
  color: #991b1b;
  margin: 0;
  line-height: 1.6;
}

/* 加载遮罩 */
.processing-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.processing-card {
  width: 260px;
  background: #fff;
  border-radius: 20px;
  padding: 32px 24px;
  text-align: center;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
}

.spinner {
  width: 56px;
  height: 56px;
  margin: 0 auto 20px;
  border: 4px solid #f0f4ff;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.processing-text {
  font-size: 14px;
  color: #666;
  margin: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Toast 提示 */
.toast {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2000;
  pointer-events: none;
}

.toast-content {
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  padding: 12px 24px;
  border-radius: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.toast-icon {
  font-size: 18px;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* 响应式设计 */
@media (max-width: 480px) {
  .page-title {
    font-size: 20px;
  }
  
  .page-subtitle {
    font-size: 12px;
  }
  
  .header-countdown {
    font-size: 13px;
    padding: 5px 14px;
  }
  
  .amount-value {
    font-size: 32px;
  }
  
  .qrcode-wrapper,
  .qrcode-placeholder {
    width: 240px;
    height: 240px;
  }
  
  .step-number {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }
  
  .warning-content {
    padding-left: 0;
  }
}
</style>
